import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Menu, MenuItem, Stack } from '@mui/material';
import Iconify from 'src/components/iconify';
import { useOrderingV2Ui } from 'src/contexts/ordering-v2-ui-context';

// ----------------------------------------------------------------------

OrderV2CategoryStrip.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
    })
  ),
  activeCategoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectCategory: PropTypes.func.isRequired,
  showMyOrder: PropTypes.bool,
};

export default function OrderV2CategoryStrip({
  categories = [],
  activeCategoryId,
  onSelectCategory,
  showMyOrder = true,
}) {
  const { openCart } = useOrderingV2Ui();
  const [moreAnchor, setMoreAnchor] = useState(null);

  const scrollTo = (id) => {
    const el = document.getElementById(`cat${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    onSelectCategory(id);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1.5,
        mb: 2,
        position: 'sticky',
        top: { xs: 0, md: 0 },
        zIndex: 9,
        bgcolor: 'background.paper',
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
        mx: { xs: -2, md: -3 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction="row"
        spacing={0}
        alignItems="center"
        sx={{
          flex: 1,
          overflowX: 'auto',
          py: 0.5,
          '&::-webkit-scrollbar': { height: 4 },
        }}
      >
        {categories.map((c) => {
          const active = activeCategoryId === c.id;
          return (
            <Button
              key={c.id}
              onClick={() => scrollTo(c.id)}
              sx={{
                flexShrink: 0,
                px: 1.5,
                py: 1,
                minWidth: 'auto',
                color: active ? '#1d1d1f' : '#86868b',
                fontWeight: active ? 700 : 500,
                fontSize: 15,
                textTransform: 'none',
                borderRadius: 0,
                borderBottom: active ? '3px solid #0e7c62' : '3px solid transparent',
                '&:hover': { bgcolor: 'transparent', color: '#1d1d1f' },
              }}
            >
              {c.name}
            </Button>
          );
        })}
        <Button
          size="small"
          onClick={(e) => setMoreAnchor(e.currentTarget)}
          endIcon={<Iconify icon="eva:chevron-down-fill" width={18} />}
          sx={{
            flexShrink: 0,
            ml: 1,
            borderRadius: 999,
            bgcolor: '#f5f5f7',
            color: '#1d1d1f',
            textTransform: 'none',
            px: 2,
            fontWeight: 600,
            '&:hover': { bgcolor: '#e8e8ed' },
          }}
        >
          See more
        </Button>
        <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
          {categories.map((c) => (
            <MenuItem
              key={c.id}
              onClick={() => {
                scrollTo(c.id);
                setMoreAnchor(null);
              }}
            >
              {c.name}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      {showMyOrder && (
        <Button
          variant="contained"
          onClick={openCart}
          sx={{
            flexShrink: 0,
            borderRadius: 999,
            px: 3,
            py: 1,
            fontWeight: 700,
            textTransform: 'none',
            bgcolor: '#f5a623',
            color: '#1d1d1f',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#e09420', boxShadow: 'none' },
          }}
        >
          My order
        </Button>
      )}
    </Box>
  );
}
